import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    DOCUMENT,
    effect,
    inject,
    Injector,
    input,
    signal,
} from '@angular/core';

import { IAccordion, IAccordionItem } from '../_shared/models/accordion/accordion.model';
import { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import { getRovingIndex } from '../_shared/utils/roving-index';
import { ContainerComponent } from '../container/container.component';
import { DISCLOSURE_TRIGGER_SELECTOR } from '../disclosure/disclosure.component';
import { HeadingComponent } from '../heading/heading.component';
import { TextHtmlComponent } from '../text-html/text-html.component';
import { AccordionItemComponent } from './accordion-item.component';

@Component({
    selector: 'sirius-accordion',
    templateUrl: './accordion.component.html',
    styleUrl: './accordion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AccordionItemComponent, ContainerComponent, HeadingComponent, TextHtmlComponent],
    host: {
        'class': 'sir-accordion',
        '(keydown)': 'onKeydown($event)',
        '(window:hashchange)': 'openHashedItem()',
    },
})
export class AccordionComponent implements IDynamicElement<IAccordion> {
    readonly config = input<IAccordion | undefined>(undefined);
    readonly items = input<IAccordionItem[] | undefined>(undefined);
    readonly multi = input<boolean | undefined>(undefined);
    readonly headline = input<string | undefined>(undefined);

    protected readonly resolvedItems = computed(() => this.config()?.items ?? this.items() ?? []);
    protected readonly resolvedMulti = computed(() => this.config()?.multi ?? this.multi() ?? false);
    protected readonly resolvedHeadline = computed(() => this.config()?.headline ?? this.headline() ?? '');

    protected readonly openState = signal<boolean[]>([]);

    private readonly _document = inject(DOCUMENT);
    private _appliedFragmentKey: string | undefined;

    constructor() {
        const injector = inject(Injector);

        // Keep the open-state vector sized to the config items, preserving
        // existing open panels when the list grows/shrinks.
        effect(() => {
            const length = this.resolvedItems().length;
            this.openState.update((current) => {
                if (current.length === length) return current;
                const next = new Array<boolean>(length).fill(false);
                for (let i = 0; i < Math.min(length, current.length); i++) {
                    next[i] = current[i];
                }
                return next;
            });
        });

        effect(() => {
            const items = this.resolvedItems();
            const id = this.locationHashId();
            if (!id || items.every((item) => item.id !== id)) return;

            afterNextRender(() => this.openHashedItem(), { injector });
        });
    }

    protected isOpen(index: number): boolean {
        return this.openState()[index] ?? false;
    }

    protected toggle(index: number, open: boolean): void {
        this.openState.update((current) => {
            const next = open && !this.resolvedMulti() ? new Array<boolean>(current.length).fill(false) : [...current];
            next[index] = open;
            return next;
        });
    }

    protected openHashedItem(): void {
        const items = this.resolvedItems();
        const id = this.locationHashId();
        if (!id) {
            this._appliedFragmentKey = undefined;
            return;
        }

        const index = items.findIndex((item) => item.id === id);
        if (index === -1) {
            this._appliedFragmentKey = undefined;
            return;
        }

        const fragmentKey = `${id}:${items.map((item) => item.id).join('\0')}`;
        if (this._appliedFragmentKey === fragmentKey) return;

        this._appliedFragmentKey = fragmentKey;
        this.toggle(index, true);
    }

    private locationHashId(): string | undefined {
        const raw = this._document.defaultView?.location.hash.slice(1);
        if (!raw) return undefined;

        try {
            return decodeURIComponent(raw);
        } catch {
            return raw;
        }
    }

    protected onKeydown(event: KeyboardEvent): void {
        const target = event.target as HTMLElement;
        if (!target.matches(DISCLOSURE_TRIGGER_SELECTOR)) return;

        const triggers = Array.from(
            (event.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(DISCLOSURE_TRIGGER_SELECTOR)
        );
        const currentIndex = triggers.indexOf(target as HTMLButtonElement);
        if (currentIndex === -1) return;

        const nextIndex = getRovingIndex(event.key, currentIndex, triggers.length, 'vertical');
        if (nextIndex === null) return;

        event.preventDefault();
        triggers[nextIndex]?.focus();
    }
}
